import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import {
  SystemFilter,
  UserFilter,
} from '../../../models/api/reports/all-filters-response';
import { ScheduledReport } from '../../../models/api/reports/all-reports-response';
import { FilterDetailResponse } from '../../../models/api/reports/filter-detail-response';
import { ScheduledReportDetails } from '../../../models/api/reports/scheduled-report-details';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { BackButtonComponent } from '../../core/buttons/back-button.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { CardComponent } from '../../core/panels/card.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { HomeCardComponent } from '../../home/home-card.component';
import { FilterViewComponent } from '../core/filter-view.component';
import { ReportFieldViewComponent } from '../core/report-field-view.component';
import { SortFieldViewComponent } from '../core/sort-field-view.component';
import { ScheduledRequestViewComponent } from './scheduled-request-view.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-scheduled-report-page',
  imports: [
    RouterLink,
    BackButtonComponent,
    FilterViewComponent,
    CardComponent,
    ActionRowComponent,
    ButtonComponent,
    PageHeaderComponent,
    ScheduledRequestViewComponent,
    ReportFieldViewComponent,
    SortFieldViewComponent,
    LoadingPanelComponent,
    HomeCardComponent,
  ],
  template: `
    <app-page-header
      [label]="scheduledReportsData.outputName"
      labelCase="none"
      labelHeader="Scheduled report"
      headerSize="h1"
    >
      <ng-template #right>
        <app-action-row>
          <app-button
            right1
            label="Edit"
            icon="fa-light fa-edit"
            title="Edit delivery details"
            (click)="editDeliveryDetails()"
          />
          <app-button
            right2
            label="Delete"
            emphasis="Low"
            (click)="confirmDelete()"
          /> </app-action-row
      ></ng-template>
    </app-page-header>

    <div class="tw-flex">
      <app-back-button
        left1
        routerLink="/reports"
        [queryParams]="{ tab: 'scheduled' }"
      >
        Back
      </app-back-button>
    </div>
    <div class="tw-grow tw-justify-center tw-w-full">
      <div class="tw-min-w-0 tw-w-[100%] tw-pt-4 sm:tw-pt-8 tw-mb-4">
        <app-home-card type="Large">
          <div class="tw-min-w-0 tw-w-[100%] tw-p-4 sm:tw-p-8">
            <app-loading-panel
              [state]="loading ? 'Loading' : 'Loaded'"
              loadingStyle="Box"
              [loadingHeight]="500"
              loadingClasses="tw-pb-8"
              [error]="error"
            >
              <ng-template #loaded>
                <div class="tw-pb-8">
                  <app-scheduled-request-view
                    [scheduledReportsData]="scheduledReportsData"
                    [scheduledReportDetails]="
                      scheduledReportsDetails.outputDetails
                    "
                  />
                </div>
                <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 sm:tw-gap-8 tw-w-full tw-pb-4 tw-min-w-0">
                  @if (filterDetails) {
                    <app-card label="Filter" class="tw-basis-1/2 tw-min-w-0 tw-overflow-hidden">
                      <app-filter-view
                        [dataSource]="filterDetails.filterDetail"
                      />
                    </app-card>
                  }
                  <div
                    class="tw-flex tw-gap-4 sm:tw-gap-8 tw-flex-col tw-min-w-0"
                    [class.tw-basis-1/2]="filterDetails"
                    [class.tw-w-full]="!filterDetails"
                  >
                    <app-card label="Report fields" labelCase="titlecase" class="tw-w-full tw-min-w-0">
                      <app-report-field-view
                        [reportFields]="scheduledReportsDetails.reportFields"
                      />
                    </app-card>
                    <app-card label="Sort fields" labelCase="titlecase" class="tw-w-full tw-min-w-0">
                      <app-sort-field-view
                        [sortBreakFields]="scheduledReportsDetails.sortFields"
                      />
                    </app-card>
                  </div>
                </div>
              </ng-template>
            </app-loading-panel>
          </div>
        </app-home-card>
      </div>
    </div>
  `,
  styles: ``,
})
export class ScheduledReportPageComponent
  extends BaseComponent
  implements OnInit
{
  private reportsService = inject(ReportsService);
  protected override router = inject(Router);

  scheduledReportsData: ScheduledReport;

  scheduledReportsDetails: ScheduledReportDetails = {
    outputDetails: [],
    reportFields: [],
    sortFields: [],
  };

  filters: (SystemFilter | UserFilter)[] = [];

  filterDetails: FilterDetailResponse;

  loading = false;
  error: any = null;

  private readonly snackbar = inject(Snackbar);

  @HandleLoadingState()
  async ngOnInit() {
    // Try to get report from service (normal navigation or sessionStorage restore)
    const report = this.reportsService.currentDetailReport() as ScheduledReport;

    // If not available, redirect back to reports list
    if (!report) {
      this.router.navigate(['/reports'], { replaceUrl: true });
      return;
    }

    this.scheduledReportsData = report;

    const filterId = this.scheduledReportsData?.filterId;
    const hasFilter = !!filterId;
    const [scheduledReportDetails, allFilters] = await Promise.all([
      this.api.reports.scheduledReportDetails(
        this.scheduledReportsData.requestId,
        'JURIS',
      ),
      this.api.filters.getAllFilters(
        'JURIS',
        this.scheduledReportsData.model,
        this.scheduledReportsData?.contract,
      ),
    ]);

    this.scheduledReportsDetails = scheduledReportDetails;

    if (hasFilter) {
      this.filterDetails = await this.api.filters.getFilterDetail(
        filterId,
        this.scheduledReportsData?.contract,
      );
      this.filters = [...allFilters.standardFilters, ...allFilters.userFilters];
      const matchedFilter = this.filters.find(
        (f) => f.filterId === this.filterDetails.filterDetail.filterId,
      );
      if (matchedFilter) {
        this.filterDetails.filterDetail.filterName = matchedFilter.filterName;
      }
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async editDeliveryDetails() {
    const hasRunOnce = !!this.scheduledReportsData.lastRunDate;

    await this.reportsService.loadScheduledRequestForEditing(
      this.scheduledReportsData.requestId,
      'JURIS',
      this.scheduledReportsData.model,
      this.scheduledReportsData.contract,
      hasRunOnce,
    );
    this.reportsService.setMode('edit-scheduled');
    this.router.navigate(['/reports/edit-report'], {
      queryParams: { step: 'delivery' },
    });
  }

  confirmDelete() {
    const nextRunDate = new Date(this.scheduledReportsData.nextRunDate)
      .toLocaleString()
      .replace(',', '');
    this.confirm.show({
      label: 'Delete scheduled report',
      message: `Are you sure you want to delete the schedule for ${
        this.scheduledReportsData.reportName + ` (Next Run: ${nextRunDate})`
      }? The report will be removed from the Scheduled Reports grid.`,
      width: '35rem',
      data: this.scheduledReportsData,
      confirm: (data) => this.confirmed(data),
    });
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async confirmed(report: ScheduledReport) {
    const response = await this.reportsService.deleteScheduledReport(
      report.requestId,
    );
    if (response.success) {
      this.snackbar.show('Scheduled report deleted', 'Success');
      this.router.navigate(['/reports'], { replaceUrl: true });
    } else {
      this.snackbar.show(
        response.message,
        'Error',
        'fa-light fa-circle-exclamation',
        5000,
      );
    }
  }
}
